import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
);

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const token = searchParams.get('token');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API_URL}/auth/reset-password`, {
                token,
                newPassword: password
            });
            setSuccess(true);
            toast.success('Password reset successfully!');
            setTimeout(() => navigate('/'), 3000);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reset password');
        }
        setLoading(false);
    };

    // Invalid Token View
    if (!token) {
        return (
            <div className="auth-page">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                    <h2 className="auth-title" style={{ color: 'var(--error)' }}>Invalid Link</h2>
                    <p className="auth-subtitle" style={{ marginBottom: '32px' }}>
                        No reset token found. The link may have expired or been used.
                    </p>
                    <button onClick={() => navigate('/forgot-password')} className="btn-primary" style={{ width: '100%' }}>
                        Request New Link
                    </button>
                </div>
            </div>
        );
    }

    // Success View
    if (success) {
        return (
            <div className="auth-page">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                    <h2 className="auth-title" style={{ color: 'var(--success)' }}>Password Reset!</h2>
                    <p className="auth-subtitle" style={{ marginBottom: '32px' }}>
                        Your password has been changed successfully. Redirecting to login...
                    </p>
                    <div className="spinner" style={{ margin: '0 auto 24px', width: '20px', height: '20px', borderTopColor: '#fff' }}></div>
                    <button onClick={() => navigate('/')} className="btn-primary" style={{ width: '100%' }}>
                        Go to Login Now
                    </button>
                </div>
            </div>
        );
    }

    // Main Form View
    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-brand fade-up delay-1">
                    <h1 className="auth-logo">infloir</h1>
                    <p className="auth-tagline">Global Task Marketplace</p>
                </div>

                <div className="fade-up delay-2">
                    <h2 className="auth-title">Reset Password</h2>
                    <p className="auth-subtitle">Enter your new password below.</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-field fade-up delay-3">
                        <label className="form-label" htmlFor="password">New Password</label>
                        <div className="input-wrap">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Min 8 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button type="button" className="icon-btn" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                    </div>

                    <div className="form-field fade-up delay-4">
                        <label className="form-label" htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                            id="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm your new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div className="fade-up delay-5">
                        <button type="submit" className="btn-primary auth-submit" disabled={loading}>
                            {loading && <span className="spinner" />}
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </div>
                </form>

                <div className="fade-up delay-6">
                    <p className="auth-footer-text" style={{ marginTop: '24px' }}>
                        Remember your password? <Link to="/">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;